/**
 * Spreadsheets and Word documents, written by hand.
 *
 * Both formats are a zip of XML files, and everything needed here is plain
 * tables of text. The libraries that do this are 300KB-700KB and would have to
 * be downloaded before Renee could save a list of 23 names, so the zip writer
 * below is about eighty lines and there is no new dependency to install, pin,
 * or have break a Cloudflare build.
 *
 * Entries are stored uncompressed. A spreadsheet of the whole roster comes to
 * a few hundred kilobytes rather than a few tens — irrelevant for something
 * that goes straight to the downloads folder, and it removes the only part of
 * the format that would be easy to get subtly wrong.
 */

// --------------------------------------------------------------------- zip

const CRC_TABLE = (() => {
  const t = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    t[n] = c >>> 0
  }
  return t
})()

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

type Entry = { name: string; data: Uint8Array }

export function zip(files: { name: string; text: string }[]): Uint8Array {
  const enc = new TextEncoder()
  const entries: Entry[] = files.map((f) => ({ name: f.name, data: enc.encode(f.text) }))

  const chunks: Uint8Array[] = []
  const central: Uint8Array[] = []
  let offset = 0

  const u16 = (v: number) => new Uint8Array([v & 0xff, (v >>> 8) & 0xff])
  const u32 = (v: number) =>
    new Uint8Array([v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff])
  const join = (parts: Uint8Array[]) => {
    const total = parts.reduce((n, p) => n + p.length, 0)
    const out = new Uint8Array(total)
    let at = 0
    for (const p of parts) {
      out.set(p, at)
      at += p.length
    }
    return out
  }

  for (const e of entries) {
    const nameBytes = enc.encode(e.name)
    const sum = crc32(e.data)
    // Fixed timestamp: 1 Jan 1980, the zero of the DOS date format. A real
    // clock here would make two exports of the same list differ byte for byte.
    const local = join([
      u32(0x04034b50), u16(20), u16(0), u16(0), u16(0), u16(0x21),
      u32(sum), u32(e.data.length), u32(e.data.length),
      u16(nameBytes.length), u16(0), nameBytes,
    ])
    chunks.push(local, e.data)

    central.push(
      join([
        u32(0x02014b50), u16(20), u16(20), u16(0), u16(0), u16(0), u16(0x21),
        u32(sum), u32(e.data.length), u32(e.data.length),
        u16(nameBytes.length), u16(0), u16(0), u16(0), u16(0), u32(0),
        u32(offset), nameBytes,
      ]),
    )
    offset += local.length + e.data.length
  }

  if (entries.length > 0xffff || offset > 0xffffffff) {
    // Both would wrap silently through their 16- and 32-bit fields and produce
    // a file that looks fine and opens empty. Neither is reachable from this
    // app; failing loudly is still cheaper than finding out on a Tuesday.
    throw new Error('Too much data for a plain zip')
  }

  const dir = join(central)
  const end = join([
    u32(0x06054b50), u16(0), u16(0),
    u16(entries.length), u16(entries.length),
    u32(dir.length), u32(offset), u16(0),
  ])
  return join([...chunks, dir, end])
}

// --------------------------------------------------------------------- xml

/** XML escaping, plus stripping the control characters Excel refuses to open. */
export function xml(v: unknown): string {
  return String(v ?? '')
    // Control characters are illegal in XML 1.0 and make Excel refuse the file
    // outright. Tab, newline and carriage return are kept.
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

// ------------------------------------------------------------------- xlsx

export type Sheet = { name: string; headers: string[]; rows: (string | number | null)[][] }

const colName = (i: number): string => {
  let s = ''
  let n = i
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s
    n = Math.floor(n / 26) - 1
  }
  return s
}

/** Sheet names cannot contain : \ / ? * [ ] and cap at 31 characters. */
const safeSheetName = (v: string) => v.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31) || 'Sheet'

function sheetXml(sheet: Sheet): string {
  const widths = sheet.headers.map((h, i) => {
    const longest = sheet.rows.reduce(
      (n, r) => Math.max(n, String(r[i] ?? '').length),
      h.length,
    )
    return `<col min="${i + 1}" max="${i + 1}" width="${Math.min(60, Math.max(10, longest + 2))}" customWidth="1"/>`
  })

  const cell = (r: number, c: number, v: string | number | null, style: number) => {
    const ref = `${colName(c)}${r}`
    if (v === null || v === '') return ''
    if (typeof v === 'number' && Number.isFinite(v)) {
      return `<c r="${ref}" s="${style}"><v>${v}</v></c>`
    }
    return `<c r="${ref}" s="${style}" t="inlineStr"><is><t xml:space="preserve">${xml(v)}</t></is></c>`
  }

  const head = `<row r="1">${sheet.headers.map((h, c) => cell(1, c, h, 1)).join('')}</row>`
  const body = sheet.rows
    .map((r, i) => `<row r="${i + 2}">${r.map((v, c) => cell(i + 2, c, v, 0)).join('')}</row>`)
    .join('')

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetPr><outlinePr/></sheetPr><cols>${widths.join('')}</cols><sheetData>${head}${body}</sheetData></worksheet>`
}

export function buildXlsx(sheets: Sheet[]): Uint8Array {
  const named = sheets.map((s, i) => ({ ...s, name: safeSheetName(s.name) || `Sheet${i + 1}` }))

  const files = [
    {
      name: '[Content_Types].xml',
      text: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${named
        .map(
          (_s, i) =>
            `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
        )
        .join('')}</Types>`,
    },
    {
      name: '_rels/.rels',
      text: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    },
    {
      name: 'xl/workbook.xml',
      text: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${named
        .map((s, i) => `<sheet name="${xml(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
        .join('')}</sheets></workbook>`,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      text: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${named
        .map(
          (_s, i) =>
            `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
        )
        .join('')}<Relationship Id="rId${named.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    },
    {
      name: 'xl/styles.xml',
      text: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles><dxfs count="0"/><tableStyles count="0" defaultTableStyle="TableStyleMedium9" defaultPivotStyle="PivotStyleLight16"/></styleSheet>`,
    },
    ...named.map((s, i) => ({ name: `xl/worksheets/sheet${i + 1}.xml`, text: sheetXml(s) })),
  ]

  return zip(files)
}

// ------------------------------------------------------- docx: Avery 5160

/**
 * Avery 5160, in twentieths of a point (1440 to the inch).
 *
 * 3 across, 10 down, each label 2.625" x 1", a 0.125" gutter between columns
 * and none between rows. The side margin is 0.1875" and the top 0.5", which is
 * what makes 3 x 2.625" + 2 x 0.125" + 2 x 0.1875" come to exactly 8.5".
 *
 * Row heights are exact, not minimum: a label that grew to fit its contents
 * would push every label after it down the sheet, and thirty of them would be
 * printed onto the gaps.
 */
const LABEL_W = 3780
const GUTTER = 180
const ROW_H = 1440
const PAGE_W = 12240
const PAGE_H = 15840
const MARGIN_TOP = 720
const MARGIN_SIDE = 270
/**
 * Zero, deliberately. Top margin + ten exact 1" rows already comes to exactly
 * the page height, so a bottom margin of any size leaves no slack: one twip of
 * rounding in Word pushes row ten onto the next page, and then every following
 * page is a row short and the whole box prints shifted.
 */
const MARGIN_BOTTOM = 0
/** Exposed so the geometry can be asserted rather than trusted. */
export const GEOMETRY = {
  labelWidth: LABEL_W,
  gutter: GUTTER,
  rowHeight: ROW_H,
  pageWidth: PAGE_W,
  pageHeight: PAGE_H,
  marginTop: MARGIN_TOP,
  marginSide: MARGIN_SIDE,
  marginBottom: MARGIN_BOTTOM,
} as const

export const PER_PAGE = 30
export const COLUMNS = 3
export const ROWS = 10

export function buildLabelsDocx(labels: string[][]): Uint8Array {
  const cellText = (lines: string[]) =>
    lines.length === 0
      ? '<w:p><w:pPr><w:spacing w:before="0" w:after="0" w:line="240" w:lineRule="auto"/></w:pPr></w:p>'
      : lines
          .map(
            (line) =>
              `<w:p><w:pPr><w:spacing w:before="0" w:after="0" w:line="240" w:lineRule="auto"/><w:ind w:left="0" w:right="0"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:sz w:val="20"/></w:rPr><w:t xml:space="preserve">${xml(line)}</w:t></w:r></w:p>`,
          )
          .join('')

  const cell = (lines: string[], width: number) =>
    `<w:tc><w:tcPr><w:tcW w:w="${width}" w:type="dxa"/><w:tcMar><w:top w:w="115" w:type="dxa"/><w:left w:w="180" w:type="dxa"/><w:bottom w:w="0" w:type="dxa"/><w:right w:w="115" w:type="dxa"/></w:tcMar><w:vAlign w:val="top"/></w:tcPr>${cellText(lines)}</w:tc>`

  // The gutter is 180 twips wide and must carry no padding of its own, or a
  // stricter layout engine than LibreOffice's widens the grid and every label
  // after the first column shifts right.
  const blank = () =>
    `<w:tc><w:tcPr><w:tcW w:w="${GUTTER}" w:type="dxa"/><w:tcMar><w:top w:w="0" w:type="dxa"/><w:left w:w="0" w:type="dxa"/><w:bottom w:w="0" w:type="dxa"/><w:right w:w="0" w:type="dxa"/></w:tcMar></w:tcPr><w:p><w:pPr><w:spacing w:before="0" w:after="0" w:line="240" w:lineRule="auto"/></w:pPr></w:p></w:tc>`

  const rows: string[] = []
  for (let i = 0; i < labels.length; i += COLUMNS) {
    const three = [labels[i] ?? [], labels[i + 1] ?? [], labels[i + 2] ?? []]
    rows.push(
      `<w:tr><w:trPr><w:trHeight w:val="${ROW_H}" w:hRule="exact"/><w:cantSplit/></w:trPr>${cell(
        three[0],
        LABEL_W,
      )}${blank()}${cell(three[1], LABEL_W)}${blank()}${cell(three[2], LABEL_W)}</w:tr>`,
    )
  }

  const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body><w:tbl><w:tblPr><w:tblW w:w="${
    LABEL_W * 3 + GUTTER * 2
  }" w:type="dxa"/><w:tblLayout w:type="fixed"/><w:tblCellMar><w:top w:w="0" w:type="dxa"/><w:left w:w="0" w:type="dxa"/><w:bottom w:w="0" w:type="dxa"/><w:right w:w="0" w:type="dxa"/></w:tblCellMar></w:tblPr><w:tblGrid><w:gridCol w:w="${LABEL_W}"/><w:gridCol w:w="${GUTTER}"/><w:gridCol w:w="${LABEL_W}"/><w:gridCol w:w="${GUTTER}"/><w:gridCol w:w="${LABEL_W}"/></w:tblGrid>${rows.join(
    '',
  )}</w:tbl><w:p><w:pPr><w:spacing w:before="0" w:after="0" w:line="14" w:lineRule="exact"/><w:rPr><w:sz w:val="2"/></w:rPr></w:pPr></w:p><w:sectPr><w:pgSz w:w="${PAGE_W}" w:h="${PAGE_H}"/><w:pgMar w:top="${MARGIN_TOP}" w:right="${MARGIN_SIDE}" w:bottom="${MARGIN_BOTTOM}" w:left="${MARGIN_SIDE}" w:header="0" w:footer="0" w:gutter="0"/></w:sectPr></w:body></w:document>`

  return zip([
    {
      name: '[Content_Types].xml',
      text: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>`,
    },
    {
      name: '_rels/.rels',
      text: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>`,
    },
    { name: 'word/document.xml', text: document },
  ])
}
